/**
 * Brand Perception « Tableau de bord » — DynamoDB row shapes + read-path contract.
 *
 * Design: [docs/plans/bp-dashboard.md](../../../../docs/plans/bp-dashboard.md) §2 (row
 * families) and §4 (API). This file is the SINGLE place the writer
 * (`fanout_synthesizer._write_bp_facts`, Python) and the reader
 * (`packages/api/src/brand-perception/`) agree on keys and field names. Every family
 * lives on `{prefix}-audit-results`.
 *
 * Deliberately types + key prefixes + the two span constants only — no fold logic, no
 * row builders. The folds live in the reader; the writes live in the agent.
 */

import { AUDIT_RESULTS_RETENTION_DAYS } from './audit';
import type { BrandQueryAxis } from './brand-query';
import type { ContenuContentType, TargetCategory } from './targets';
import type { ScoreKind } from '../utils/scoreRule';

// ---------------------------------------------------------------------------
// Key prefixes
// ---------------------------------------------------------------------------

/** `PK = BRAND#<brandId>`, `SK = BPSERIES#<axis>` — one item per brand × axis. */
export const BP_SK_SERIES = 'BPSERIES#';

/** `SK = BPTREND#<axis>#<YYYY-MM-DD>` — daily breakdowns, 90 d TTL. */
export const BP_SK_TREND = 'BPTREND#';
/** `SK = BPPROMPT#<axis>#<YYYY-MM-DD>` — per-prompt daily facts, 90 d TTL. */
export const BP_SK_PROMPT = 'BPPROMPT#';
/** `SK = BPTOP#<axis>#<YYYY-MM-DD>` — daily target leaderboard, 90 d TTL. */
export const BP_SK_TOP = 'BPTOP#';

/**
 * 00b DQ9 sharded per-prompt facts (prompt finisher). Pointer `BPPROMPTP#<axis>#<day>`
 * `{ foldStartedAt, shards }` → shards `BPPROMPTS#<axis>#<day>#<foldStartedAt>#<i>`
 * `{ entries }`, 90 d TTL; the month grain `BPPROMPTMP#<axis>#<YYYY-MM>` →
 * `BPPROMPTMS#<axis>#<YYYY-MM>#<foldStartedAt>#<i>`, 730 d TTL. A day with a pointer is
 * read from it; a day without keeps its legacy `BPPROMPT#` row.
 */
export const BP_SK_PROMPT_POINTER = 'BPPROMPTP#';
export const BP_SK_PROMPT_SHARD = 'BPPROMPTS#';
export const BP_SK_PROMPT_MONTH_POINTER = 'BPPROMPTMP#';
export const BP_SK_PROMPT_MONTH_SHARD = 'BPPROMPTMS#';

/** `SK = BPTRENDM#<axis>#<YYYY-MM>` — monthly breakdowns (Lot 9). */
export const BP_SK_TREND_MONTHLY = 'BPTRENDM#';
/** `SK = BPPROMPTM#<axis>#<YYYY-MM>` — monthly per-prompt facts (Lot 9). */
export const BP_SK_PROMPT_MONTHLY = 'BPPROMPTM#';
/** `SK = BPTOPM#<axis>#<YYYY-MM>` — monthly target leaderboard (Lot 9). */
export const BP_SK_TOP_MONTHLY = 'BPTOPM#';

/** `PK = BRANDTGT#<brandId>`, `SK = <axis>#<targetId>` — per-target history. */
export const BP_SK_TARGET_FLOOR = 'BPTGTFLOOR#';
export const BP_PK_TARGET_AGG = 'BRANDTGT#';

/**
 * Closes a forward-ISO prefix range: `SK BETWEEN '<prefix><from>' AND '<prefix><to>~'`.
 * `~` (0x7E) sorts above every digit and `-`, so the upper bound covers `<to>` itself
 * whatever follows it. The day keys are forward ISO — NOT the `_reverse_iso` trick
 * `ENGINE_INFLUENCE#` uses — because the access pattern is always a range, never
 * "newest first".
 */
export const BP_SK_RANGE_END = '~';

/**
 * `BPTGTAGG` stores one FLAT attribute per month and per measure (`c_2026_10`,
 * `p_2026_10`, `d_2026_10`) rather than a nested `{ months: … }` map, because `ADD`
 * into an absent nested path throws. Writer and reader must derive the suffix the
 * same way, so it lives here.
 */
export function bpMonthAttrSuffix(month: string): string {
  return month.replace('-', '_');
}

// ---------------------------------------------------------------------------
// Span constants
// ---------------------------------------------------------------------------

/**
 * Longest window the dashboard will serve. Tied to the retention constant on
 * purpose (§9: "`maxDays` stays consistent with the retention constant") — a
 * selector offering more days than we keep rows for would draw a flat line and
 * call it a measurement. A longer request is CLAMPED and the clamp is reported.
 */
export const BP_DASHBOARD_MAX_SPAN_DAYS = AUDIT_RESULTS_RETENTION_DAYS;

/**
 * The tier boundary, one line, no exceptions: a requested span of ≤90 days reads
 * the DAILY families, >90 days reads the MONTHLY ones. Never both in one response —
 * a chart carrying two precisions is a bug.
 */
export const BP_DASHBOARD_DAILY_TIER_MAX_DAYS = 90;

/**
 * Cross-brand overlay cap — the number of **OVERLAY lines**, NOT the total plotted.
 * The primary brand is additional and is never counted here: it is drawn in `VIZ.ink`,
 * so it consumes no `VIZ.series` hue. Total lines on the chart = this + 1. Each brand
 * costs one `BPSERIES` GetItem (§7).
 *
 * Bound by the RENDERER, not by the read budget: `VIZ.series` in the frontend viz
 * tokens has exactly 4 colours, so a 5th OVERLAY reuses a hue and two brands silently
 * share one — a chart asserting something false. This constant is the single source of
 * truth for the UI's multi-select cap, the DTO's CSV cap and the API's 400 boundary;
 * they cannot be allowed to drift. Raising it means picking more distinguishable
 * accessible hues first.
 *
 * Charging the primary against this budget made the documented maximum unreachable
 * (4 overlays passed the DTO and were then rejected by the service) — do not
 * re-introduce it into the count.
 */
export const BP_DASHBOARD_MAX_OVERLAY_BRANDS = 4;

// ---------------------------------------------------------------------------
// §2.2 BPSERIES — the hot row
// ---------------------------------------------------------------------------

/**
 * One day of the rolling window. Positional and scalars-only by design: this row is
 * read by EVERY dashboard load and by the whole cross-brand chart, so it must stay
 * ~17 KB whatever the window length.
 *
 * `[score, citationRate, recommendationRate, sourceRate, n, partial]`. `null` (the
 * slot itself) = no audit that day → the line BREAKS. Never `0`. The three rates are
 * individually `null` when the axis does not measure them, or when the denominator
 * was empty — a `0` there would read as a measurement.
 *
 * ⚠ THE THREE RATES ARE STORED AS 0-100 INTEGERS (`bp_facts._pct`, the convention
 * they share with `score`), while every rate on the dashboard RESPONSE is a 0-1
 * fraction. `BrandPerceptionService.fromPct` is the one boundary that converts;
 * reading a slot straight into a response field renders « 8 800 % ».
 *
 * `partial` is positional rather than a separate attribute so the marker survives the
 * scalars-only budget. `null` or ABSENT = complete, which is what the 5-element slots
 * written before this member existed were.
 *
 * Members 6-7 (narrative only, F step 8b): `scoreImageV1` / `nImageV1`, the scored
 * `image_v1` rows' mean and count, appended ONLY when the day holds an `image_v1` row
 * (a 6-member slot = none). On such a day `score` is the `rubric_v0` rows' score and
 * is `null` when there is none: the two rules never share one mean.
 */
export type BPSeriesSlot =
  | null
  | [
      score: number | null,
      citationRate: number | null,
      recommendationRate: number | null,
      sourceRate: number | null,
      n: number,
      partial?: true | null,
      scoreImageV1?: number | null,
      nImageV1?: number | null,
    ];

export interface BPSeriesRow {
  tenantId: string;
  brandId: string;
  axis: BrandQueryAxis;
  /** UTC day of `slots[0]`. The array is a fixed `BP_DASHBOARD_MAX_SPAN_DAYS` window. */
  startDay: string;
  slots: BPSeriesSlot[];
  lastDay?: string;
  lastAuditId?: string;
  lastCompletedAt?: string;
  /** OCC token for the read-modify-write. */
  version: number;
}

// ---------------------------------------------------------------------------
// §2.3 BPTREND — daily breakdowns
// ---------------------------------------------------------------------------

/**
 * F step 8b-bis: a narrative bucket holding scored `image_v1` rows carries their mean
 * (`score_image_v1`) and count (`n_image_v1`) BESIDE `score` (the `rubric_v0` rows),
 * never folded into it. Both absent when the bucket has no such row.
 */
interface BPImageV1Sibling {
  score_image_v1?: number;
  n_image_v1?: number;
}

/**
 * `n` is OPTIONAL because `scripts/backfill-bp-trend.ts` cannot derive it — history
 * records no per-engine response count — so a backfilled row carries `{score, cited}`
 * only. An absent `n` must never become a `0` denominator: it means "unknown", and the
 * forward path always writes it.
 */
export interface BPEngineBucket extends BPImageV1Sibling {
  score: number | null;
  cited?: number;
  recommended?: number;
  n?: number;
}
export interface BPZoneBucket extends BPImageV1Sibling {
  score: number | null;
  cited?: number;
  n?: number;
}
export interface BPLanguageBucket extends BPImageV1Sibling {
  score: number | null;
  n: number;
}
export interface BPLevelBucket {
  score: number | null;
  cited?: number;
  n?: number;
}
export interface BPTagBucket extends BPImageV1Sibling {
  score: number | null;
  cited?: number;
  recommended?: number;
  promptCount: number;
}

export interface BPSentimentCounts {
  positive: number;
  neutral: number;
  negative: number;
}
export interface BPAlignmentCounts {
  strong: number;
  partial: number;
  weak: number;
  misaligned: number;
}

/**
 * The latest audit's prose, carried on the row so the dashboard never reads a fat
 * `AGENT_DONE#` summary item.
 *
 * Written on the **narrative axis only** — absent on authority and referral is the
 * NORMAL case there, not missing data, and the reader must not report it as a gap.
 * Write-time caps (owned by the writer): text ≤800 chars, ≤5 list items of ≤300 chars
 * each. An empty value is an ABSENT field, never `{}` or `[]`.
 */
export interface BPPerception {
  keyPerception?: string;
  pros?: string[];
  cons?: string[];
}

export interface BPTrendRow {
  tenantId: string;
  brandId: string;
  axis: BrandQueryAxis;
  /** `YYYY-MM-DD` on the daily tier, `YYYY-MM` on the monthly one. */
  day: string;
  auditId?: string;
  completedAt?: string;

  /** On a narrative day holding an `image_v1` row: the `rubric_v0` rows' score only,
   *  ABSENT when there is none (F step 8b). */
  score?: number;
  promptCount: number;
  engineCount?: number;
  /** Narrative, F step 8b: mean / count of the scored `image_v1` rows. Both ABSENT
   *  when the day has none. */
  score_image_v1?: number;
  n_image_v1?: number;
  scoreRules?: { rubric_v0?: number; image_v1?: number };
  criteriaAddressed?: number;
  /** Comparaison (F step 8b): preference counts over scored rows, and the criteria won (sum). */
  preference?: { won: number; tie: number; lost: number; not_named: number };
  criteriaWon?: number;

  /**
   * 0-100 INTEGERS, like `score` and like the `BPSERIES` slots — NOT fractions. The
   * response normalises them (`fromPct`); nothing else may read them raw.
   *
   * ABSENT — not `0` — when the axis does not measure it AND when the denominator was
   * empty (the base summaries return a bare `0.0` there, which would read as a
   * measurement). Axis applicability, enforced by the writer and its tests:
   * narrative has no rates / `byLevel` / `topics`; authority has no
   * `recommendationRate` / `sentiment` / `alignment` / `byLevel` / `perception`;
   * referral has no `sourceRate` / `alignment` / `topics` / `perception`.
   * No consumer may substitute `0` for any of them.
   */
  citationRate?: number;
  recommendationRate?: number;
  sourceRate?: number;

  sentiment?: BPSentimentCounts;
  alignment?: BPAlignmentCounts;

  byEngine?: Record<string, BPEngineBucket>;
  byZone?: Record<string, BPZoneBucket>;
  byLanguage?: Record<string, BPLanguageBucket>;
  /** referral only — the L1–L5 ladder. */
  byLevel?: Record<string, BPLevelBucket>;
  /** Prompt tags, pre-folded at WRITE time (D6). ≤ `BRAND_TAG_REGISTRY_MAX` keys. */
  byTag?: Record<string, BPTagBucket>;
  /** `byTag` hit the 100-key cap and is a prefix — the reader must say so. */
  byTagTruncated?: true;

  /** authority only — ≤30 each. */
  topics?: { cited: string[]; missing: string[] };

  /** narrative and comparison (the comparison synthesis, F step 7b); absent when it failed. */
  perception?: BPPerception;

  /**
   * The two reasons `byTag` does not cover `promptCount`. They are DIFFERENT numbers
   * and neither is ever folded into a « sans tag » bucket (§2.1):
   *   - `unknownPrompts` — the `queryId` no longer exists in the prompts table
   *     (hard-deleted), so its tags are unrecoverable.
   *   - `untaggedPrompts` — a live prompt that simply carries no tag.
   * Tag coverage = `promptCount - unknownPrompts - untaggedPrompts`.
   *
   * Both are genuinely computed counts, so `0` is honest HERE specifically — it is a
   * measurement, not the forbidden fallback. Absent means the row predates the field.
   */
  unknownPrompts?: number;
  untaggedPrompts?: number;

  /** `status == 'partial'` audit — the day's point is real but incomplete. */
  partial?: true;

  /** Written only by `scripts/backfill-bp-trend.ts`: a thin row, not a forward-path
   *  one (no per-engine `n`, no prose). */
  backfilled?: true;
}

// ---------------------------------------------------------------------------
// §2.4 BPPROMPT — per-prompt daily facts
// ---------------------------------------------------------------------------

/**
 * Short keys on purpose: ~60 B × 1 000 prompts ≈ 60 KB per day.
 * `qid`=queryId, `h`=queryTextHash, `s`=score, `c`=citation rate,
 * `r`=recommendation rate, `a`=alignment, `se`=sentiment, `bp`=bestPosition.
 */
export interface BPPromptFact {
  qid: string;
  h?: string;
  /** ABSENT on an unscored prompt (F step 8b-bis): a point with no value, never `0`. */
  s?: number;
  /** The entry's `scoreKind`, classified prompts only. A prompt's series breaks where
   *  it changes (`scoreSeriesKey`, F step 8d). */
  k?: ScoreKind;
  /** Comparaison: the entry's dominant `preference`. */
  pr?: string;
  /** The source audit when every row of the entry has one… */
  aid?: string;
  /** …else the source audit per engine (00b DQ9). */
  ae?: Record<string, string>;
  /** Month entries (BPPROMPTMS, F step 8f): the month's days left out because their `(h, k)`
   *  differs from the newest day's. Absent when 0. */
  od?: number;
  c?: number;
  r?: number;
  a?: string;
  se?: string;
  bp?: number;
  /** storedPlaceKey at dispatch — absent on facts written before lot E deploy 1. */
  z?: string;
  /** languageKey at dispatch — absent on facts written before lot E deploy 1. */
  l?: string;
}

export interface BPPromptRow {
  tenantId: string;
  brandId: string;
  axis: BrandQueryAxis;
  day: string;
  auditId?: string;
  completedAt?: string;
  /** ≤1000. */
  prompts: BPPromptFact[];
  truncated?: true;
  /** `status == 'partial'` audit — the column is real but incomplete. */
  partial?: true;
}

// ---------------------------------------------------------------------------
// §2.6 BPTOP — daily target leaderboard
// ---------------------------------------------------------------------------

/**
 * `id`=targetId, `l`=label, `u`=the cible's own page, `ct`=category, `c`=citations,
 * `p`=prompts citing it, `tg`=Cibles tag ids, `ty`=contenu content type,
 * `pa`=publishedAt, `rp`=related product target ids, `d`=citations per apex domain.
 */
export interface BPTopEntry {
  id: string;
  l?: string;
  /**
   * RESPONSE-ONLY and, in practice, ABSENT: nothing fills it. A writer must not emit
   * it either — a url is a catalogue property that has to stay resolvable at READ
   * time, not be frozen onto every historical fact row (D6b).
   *
   * The leaderboard gets its labels and urls from `POST …/targets/by-id` instead,
   * run by the dashboard for the ids on screen (see `BPTargetsView`'s `labels` prop
   * and docs/features/targets.md §19-§20). `l` and `u` are only a fallback for a
   * response that somehow carries them.
   */
  u?: string;
  ct?: TargetCategory;
  c: number;
  p?: number;
  tg?: number[];
  ty?: ContenuContentType;
  pa?: string;
  rp?: string[];
  /**
   * The target's BUSIEST apex domains only, capped at `BP_TOP_DOMAINS_PER_ENTRY` (10)
   * for the item bound. Display-only: the row's `byDomain` rollup is folded from the
   * UNCAPPED per-target map, so it stays exact. Never read `|d|` as "this target is
   * cited on N domains" — it saturates at 10.
   */
  d?: Record<string, number>;
}

/** `c` = citations, `targets` = DISTINCT targets. Distinct counts are per-day and
 *  therefore NOT additive over a window — the reader takes a max and says so. */
export interface BPTopBucket {
  c: number;
  targets: number;
}

/**
 * A day with NO Cibles measurement writes no `BPTOP` row at all — never a
 * `citedTotal: 0` one, which would read « 0 cibles citées » for « jamais mesuré ».
 * `BPTREND` is still written for that day, so an absent `BPTOP` inside a window means
 * "not measured" and must not be folded as a zero.
 */
export interface BPTopRow {
  tenantId: string;
  brandId: string;
  axis: BrandQueryAxis;
  day: string;
  auditId?: string;
  completedAt?: string;
  responseTotal?: number;
  promptTotal?: number;
  /** Distinct cited targets — the honest total `top[]` may be a prefix of. */
  citedTotal?: number;
  /**
   * ≤500, by `c` desc.
   *
   * On a day with TWO audits this row keeps the LAST (full Put under a
   * `completedAt <= :mine` guard) while `BPTGTAGG` counts the FIRST (first-writer-wins
   * per day, because an `ADD`-built total cannot be retracted). Not a bug: the
   * consequence is that a month is NOT the sum of its days' `top[]`, and the two
   * families may disagree on such a day.
   */
  top: BPTopEntry[];
  /** Folded over EVERY cited target, so these rollups are exact (§2.6). */
  byDomain?: Record<string, BPTopBucket>;
  byTag?: Record<string, BPTopBucket>;
  byCategory?: Partial<Record<TargetCategory, BPTopBucket>>;
  /**
   * NEVER WRITTEN today and cannot be: it is keyed on `ty`, a non-compiled catalogue
   * field the synthesizer cannot reach (like `l`, `pa`, `rp`, which are absent rather
   * than empty for the same reason). Do not synthesize it from `top[]`.
   */
  byContentType?: Partial<Record<ContenuContentType, BPTopBucket>>;
  /**
   * `citedTotal > 500` → `top` is a prefix. The rollups above are folded over the
   * COMPLETE cited set, so `topTruncated` does not make them approximate — but they
   * carry key caps of their own, hence the two markers below.
   */
  topTruncated?: true;
  /** `byDomain` hit its 200-key cap — the 201st sales channel is missing. */
  byDomainTruncated?: true;
  /** `byTag` hit its 100-key cap. Same reason, same 400 KB item bound. */
  byTagTruncated?: true;
  /**
   * The D6b re-fold job could not honestly re-fold `byTag`, so it left the previous
   * value in place: on a `topTruncated` row, re-folding from the 500-row prefix would
   * under-count a rollup that was originally exact over the complete cited set.
   * Stale and labelled beats quietly wrong.
   */
  byTagStale?: true;
  /** `status == 'partial'` audit: fewer responses were measured, so `citedTotal` and
   *  every rollup here are UNDERCOUNTS, not findings. */
  partial?: true;
}

// ---------------------------------------------------------------------------
// BPTGTFLOOR — the earliest day target data exists for
// ---------------------------------------------------------------------------

/**
 * Write-once row at `SK = BPTGTFLOOR#<axis>`, TTL +730 d so it outlives the 90-day
 * dailies.
 *
 * `BPTOP` / `BPTGTAGG` are NOT backfillable — history carries no `targetDomains` and
 * `sources` was capped at 20 — so without this row a window reaching before the first
 * measured day renders as an empty chart, which reads as "no targets are ever cited".
 * The target tabs state « Données disponibles à partir du ⟨firstDay⟩ » instead.
 */
export interface BPTargetFloorRow {
  tenantId: string;
  brandId: string;
  axis: BrandQueryAxis;
  /** `YYYY-MM-DD` of the first audit that ever wrote target data for this axis. */
  firstDay: string;
  firstAuditId?: string;
}

// ---------------------------------------------------------------------------
// §2.7 BPTGTAGG — per-target history
// ---------------------------------------------------------------------------

export interface BPTargetAggMeta {
  tenantId: string;
  brandId: string;
  axis: BrandQueryAxis;
  targetId: string;
  l?: string;
  ct?: TargetCategory;
  tg?: number[];
  ty?: ContenuContentType;
  pa?: string;
  lastDay?: string;
  lastSeenAt?: string;
}

/**
 * Flat per-month attributes alongside the metadata: `c_<YYYY>_<MM>` (citations,
 * number), `p_<YYYY>_<MM>` (prompts, number), `d_<YYYY>_<MM>` (citations per apex
 * domain). See `bpMonthAttrSuffix`.
 */
export type BPTargetAggRow = BPTargetAggMeta & Record<string, unknown>;

// ---------------------------------------------------------------------------
// §4 API response contract
// ---------------------------------------------------------------------------

/** Which tier served the response. Points, breakdowns and segments all carry this
 *  grain — a response never mixes the two. */
export type BPDashboardGrain = 'day' | 'month';

export const BP_DASHBOARD_SEGMENT_BY = [
  'tag',
  'engine',
  'zone',
  'language',
  'level',
  'brand',
  'none',
] as const;
export type BPDashboardSegmentBy = (typeof BP_DASHBOARD_SEGMENT_BY)[number];

/**
 * Machine-readable statements about what the fold could NOT do exactly. Every code
 * here MUST be surfaced by the card (the `kpiUnfiltered` precedent) — a narrowing
 * the numbers do not actually reflect is worse than no filter at all.
 */
export type BPDashboardCaveat =
  /** `tags` is an OR over pre-folded per-tag buckets: a prompt carrying two selected
   *  tags contributes twice and cannot be de-duplicated at read time (D6). */
  | 'tags_or_overlap'
  /** The requested filter dimension only carries a score in the trend rows, so the
   *  citation / recommendation / source rates are not available under it. */
  | 'rates_unavailable_under_filter'
  /**
   * A filter is active, but the `by*` maps (and `perception` / `topics` / `sentiment` /
   * `alignment`) describe the UNFILTERED population. They are pre-folded write-time
   * rollups, not a cross-tab — the same reason two narrowing dimensions are a 400 — so
   * narrowing them at read time is structurally impossible. The headline numbers ARE
   * filtered, so the card must say which half of the page the filter reached.
   */
  | 'breakdowns_unfiltered'
  /** Span >90 d: the monthly tier is the only legal source and it is not written yet
   *  (Lot 9), so breakdowns / segments / prompt and target views are EMPTY. Not an
   *  error, and explicitly not back-filled from the daily rows. */
  | 'monthly_tier_empty'
  /** A rollup was re-folded from the day's `top[]` prefix instead of the exact
   *  whole-catalogue rollup, because the request narrowed the target set. */
  | 'rollup_from_top_n'
  /** A distinct-entity count was folded over several days, where it is not additive;
   *  the figure reported is the per-day maximum, i.e. a lower bound. */
  | 'distinct_counts_are_per_day_max'
  /** At least one day's `byTag` hit the 100-key write cap, so the tag breakdown is a
   *  prefix of the brand's tags — never a silent truncation. */
  | 'by_tag_truncated'
  /** At least one row in the window came from `scripts/backfill-bp-trend.ts`: thin by
   *  construction (no per-engine `n`, no prose), so `n`-weighted reads are short. */
  | 'backfilled_rows_in_window'
  /** `byDomain` hit its 200-key write cap on at least one day — a sales channel beyond
   *  the 200th is missing from the channel rollup. */
  | 'by_domain_truncated'
  /** At least one day's `byTag` is a STALE value the D6b re-fold job declined to
   *  rewrite (it could not re-fold honestly from a truncated `top[]`). */
  | 'by_tag_stale'
  /**
   * The requested window starts BEFORE the first day target data exists
   * (`BPTGTFLOOR#`). `BPTOP`/`BPTGTAGG` are not backfillable, so the early part of the
   * window is unmeasured, not empty — the card states « Données disponibles à partir
   * du ⟨availableFrom⟩ ».
   */
  | 'window_precedes_target_data'
  /** At least one day in the window has no `BPTOP` row: target data was NOT measured
   *  that day, which is not the same as nothing being cited. */
  | 'target_days_unmeasured'
  /** Prompt matrix, window past the daily tier: the columns older than the retained
   *  `BPPROMPT` rows carry the per-prompt SCORE only (from the audit summaries) and
   *  cannot be opened. */
  | 'prompt_detail_score_only'
  /** …and there were more such score-only audit days than the column budget: the
   *  oldest were left out. */
  | 'score_only_columns_truncated'
  /** Narrative: the window holds a bucket scored under the `image_v1` rule. Its score
   *  is drawn separately (`scoreImageV1`), never averaged with `score` (F step 8b). */
  | 'score_rule_boundary';

/**
 * ⚠ UNITS, and they differ WITHIN these two objects: `score` is 0-100 (the chart draws
 * it against `yMax={100}` and formats it with `fmtInt`), while the three rates are 0-1
 * FRACTIONS — what `fmtPct` multiplies and what the Conversations dashboard returns for
 * the same measurements. The stored rows hold the rates as 0-100 integers instead; the
 * service's `fromPct` is the only place that crosses between the two.
 */
export interface BPDashboardPoint {
  /** `YYYY-MM-DD` at `grain: 'day'`, `YYYY-MM` at `grain: 'month'`. */
  day: string;
  /** 0-100. */
  score: number | null;
  /** 0-1 fraction. */
  citationRate?: number | null;
  /** 0-1 fraction. */
  recommendationRate?: number | null;
  /** 0-1 fraction. */
  sourceRate?: number | null;
  n: number;
  /** 0-100, narrative: the `image_v1` rows' score, weighted by `n_image_v1`. ABSENT
   *  on a bucket without such rows (F step 8b). Never folded into `score`. */
  scoreImageV1?: number;
}

/** Same units as `BPDashboardPoint`: `score` 0-100, the rates 0-1 fractions. */
export interface BPDashboardKpis {
  score: number | null;
  citationRate?: number | null;
  recommendationRate?: number | null;
  sourceRate?: number | null;
  n: number;
  /** See `BPDashboardPoint.scoreImageV1`. */
  scoreImageV1?: number;
}

export interface BPDashboardResponse {
  axis: BrandQueryAxis;
  /** The range actually served — equal to the request unless `clamped`. */
  from: string;
  to: string;
  grain: BPDashboardGrain;
  /** Request span exceeded `BP_DASHBOARD_MAX_SPAN_DAYS`; `from` was moved forward. */
  clamped?: true;

  points: BPDashboardPoint[];
  /** Overlay lines, materialized on the SAME day axis as `points`. */
  byBrand?: Record<string, { brandName: string; points: (number | null)[] }>;
  bySegment?: Record<string, { points: (number | null)[]; promptCount: number }>;

  /** Index-aligned to `points`; `null` where that bucket has no row. */
  sentiment?: (BPSentimentCounts | null)[];
  alignment?: (BPAlignmentCounts | null)[];
  topics?: { cited: string[]; missing: string[] };

  byEngine?: Record<string, BPEngineBucket>;
  byZone?: Record<string, BPZoneBucket>;
  byLanguage?: Record<string, BPLanguageBucket>;
  byLevel?: Record<string, BPLevelBucket>;
  byTag?: Record<string, BPTagBucket>;

  current: BPDashboardKpis | null;
  /** The equal-length window immediately before. A SINGLE `null` — not zeros — when
   *  nothing comparable exists. */
  prev: BPDashboardKpis | null;

  /** `null` on authority and referral is NORMAL — the writer only produces prose on
   *  the narrative axis. Not a gap, and not to be reported as one. */
  perception: BPPerception | null;
  /**
   * The two reasons tag coverage is short of `n`, kept distinct (see `BPTrendRow`).
   * Absent when no row that could report them was read; a `0` here is a real count.
   */
  unknownPrompts?: number;
  untaggedPrompts?: number;
  /**
   * Buckets whose point came from a `status == 'partial'` audit — « données
   * partielles ». Read from the `BPSERIES` slot's 6th member, so it is available on
   * the default (series-only) load; the trend rows corroborate it when read.
   */
  partialBuckets?: string[];
  caveats: BPDashboardCaveat[];
}

export interface BPPromptMatrixColumn {
  day: string;
  auditId?: string;
  partial?: true;
  /**
   * The day's `BPPROMPT` row has expired (90 d) and the column was rebuilt from the
   * audit's `AGENT_DONE#` summary (730 d): its cells carry ONLY `s` — the per-prompt
   * score is exact there, but the citation/reco/alignment fields are re-checked
   * against engine rows at write time and cannot be re-derived. Not openable.
   */
  scoreOnly?: true;
  /**
   * 00b DQ9 month grain: ONE column for the month, read from its written
   * `BPPROMPTMP` pointer (never folded at read time); `day` = the month's first day.
   */
  month?: true;
}

export interface BPPromptMatrixRow {
  /** `queryTextHash` — kept for backwards compat (contentActionId still uses it). */
  h: string;
  /** Most recent `queryId` seen for this hash. */
  qid: string;
  /** Identity key = promptIdentityKey(textHash, storedPlaceKey, languageKey) of the
   *  prompt's CURRENT stored place/language. Added by lot E deploy 1. */
  key: string;
  /** Every queryId folded into this row (drill-down filter). Added by lot E deploy 1. */
  qids: string[];
  text?: string;
  tags?: string[];
  /** Absent from the prompts table — counted in totals, excluded from tag series. */
  unknownPrompt?: true;
  /** Index-aligned to `columns`. */
  cells: (BPPromptFact | null)[];
}

export interface BPPromptMatrixResponse {
  axis: BrandQueryAxis;
  from: string;
  to: string;
  grain: BPDashboardGrain;
  clamped?: true;
  columns: BPPromptMatrixColumn[];
  rows: BPPromptMatrixRow[];
  unknownPrompts: number;
  truncated?: true;
  /** Columns from a partial audit — fewer prompts were measured. */
  partialBuckets?: string[];
  caveats: BPDashboardCaveat[];
}

/**
 * The axis scope of the Cibles reads (`dashboard/targets`, `dashboard/contents`).
 * `'all'` reads the three axes' `BPTOP` rows and merges them by `targetId`, exactly as
 * one axis's days are merged: citations (`c`) add up, and a prompt belongs to ONE axis,
 * so `p` adds up across axes too. Distinct-target counts are unions of the `top[]` ids
 * (exact unless a row is `topTruncated`), never sums of the per-axis counts.
 */
export type BPTargetsAxis = BrandQueryAxis | 'all';

export interface BPTargetsResponse {
  axis: BPTargetsAxis;
  from: string;
  to: string;
  grain: BPDashboardGrain;
  clamped?: true;
  /** Sums over the window. */
  responseTotal: number;
  promptTotal: number;
  /** Per-day maximum of a distinct count — a lower bound, not a sum. */
  citedTotalMax: number;
  /** Merged leaderboard — a top-N-of-top-N over several days. */
  top: BPTopEntry[];
  /** `c` summed; `targets` = per-day max. `share` = `c / Σ c`. */
  byDomain: Record<string, BPTopBucket & { share: number | null }>;
  byTag: Record<string, BPTopBucket>;
  byCategory: Partial<Record<TargetCategory, BPTopBucket>>;
  /** Always EMPTY in practice: `BPTopRow.byContentType` is never written and `ty` is
   *  absent from `top[]`, so there is nothing to fold. Never synthesized. */
  byContentType: Partial<Record<ContenuContentType, BPTopBucket>>;
  /** « Tag le plus fort » per channel. Computed from `top[]` only, so it carries
   *  `rollup_from_top_n` whenever `topTruncated`. */
  topTagByDomain: Record<string, { tag: number; c: number }>;
  topTruncated?: true;
  /**
   * First day target data exists for this brand × axis, from `BPTGTFLOOR#`. Absent
   * when no target audit has ever run — then there is no floor to state either, and
   * the card says « jamais mesuré » rather than naming a date.
   */
  availableFrom?: string;
  /** Buckets in the window whose `BPTOP` row came from a partial audit: every count
   *  below is an UNDERCOUNT for those, not a finding. */
  partialBuckets?: string[];
  /** Buckets in the window with NO `BPTOP` row — target data was not measured. */
  unmeasuredBuckets?: string[];
  caveats: BPDashboardCaveat[];
}

export interface BPTargetHistoryResponse {
  axis: BrandQueryAxis;
  targetId: string;
  from: string;
  to: string;
  /** Always `'month'`: `BPTGTAGG` is month-grained whatever the span. */
  grain: 'month';
  clamped?: true;
  label?: string;
  category?: TargetCategory;
  tags?: number[];
  contentType?: ContenuContentType;
  publishedAt?: string;
  months: Array<{
    month: string;
    citations: number;
    prompts: number | null;
    byDomain: Record<string, number>;
  }>;
  caveats: BPDashboardCaveat[];
}

// ---------------------------------------------------------------------------
// GET …/dashboard/contents — the « Contenus » view (docs/features/brand-perception.md §9.13)
// ---------------------------------------------------------------------------

/**
 * This endpoint's OWN grain (day ≤ 31 d, ISO week 32–90 d, month > 90 d), not
 * `BPDashboardGrain`. Bucket keys: day `YYYY-MM-DD`; week = its Monday `YYYY-MM-DD` (UTC);
 * month `YYYY-MM`. The week is a UNION of the daily `top[]` contenu ids — never a sum.
 */
export type BPContentsGrain = 'day' | 'week' | 'month';

/** One MEASURED bucket of the line. An unmeasured bucket has NO point (a gap, never 0). */
export interface BPContentsLinePoint {
  bucket: string;
  /** Distinct contents cited in the bucket. */
  contentsCited: number;
  /** Citations (summable across days). */
  citations: number;
  /** Days of the bucket that carry a measurement. ABSENT at `month` grain unless the
   *  written monthly row states it (Lot 9) — never a guessed `0`. */
  measuredDays?: number;
  /** Edge bucket covering fewer days than the grain (window start/end). Only ever `true`. */
  partial?: true;
  /** A day of the bucket hit `BP_TOP_MAX`: `contentsCited` is a lower bound. */
  topTruncated?: true;
}

/** Contents PUBLISHED in a bucket (by `publishedAt`, never `createdAt`). */
export interface BPContentsDotBucket {
  bucket: string;
  total: number;
  /** Edge bucket the window cuts: it counts fewer days' publications than the grain.
   *  Only ever `true` — same rule as `BPContentsLinePoint.partial`. */
  partial?: true;
  byContentType: Partial<Record<ContenuContentType | 'unknown', number>>;
}

/** Day grain only — one dot per content. */
export interface BPContentsDot {
  targetId: string;
  publishedAt: string;
  contentType?: ContenuContentType;
  title?: string;
}

export interface BPContentsTableRow {
  targetId: string;
  /** Citations over the window. */
  c: number;
  label?: string;
  url?: string;
  contentType?: ContenuContentType;
  publishedAt?: string;
  /** Resolve through `POST …/targets/by-id`, for the visible rows only. */
  relatedTargetIds?: string[];
  /** Cited historically but absent from the current content shards (deleted since, or a
   *  mislabelled legacy shard) — « Cible non résolue », never dropped. */
  unresolved?: true;
}

export interface BPContentsResponse {
  axis: BPTargetsAxis;
  from: string;
  to: string;
  grain: BPContentsGrain;
  clamped?: true;
  line: BPContentsLinePoint[];
  dots: {
    buckets: BPContentsDotBucket[];
    /** Day grain only. */
    items?: BPContentsDot[];
    /** `items` was capped. */
    truncated?: true;
    /** Contents with no `publishedAt` — « N contenus sans date ». */
    undated: number;
  };
  table: BPContentsTableRow[];
  unresolvedCount: number;
  /** Contents found in the brand's content shards. */
  contentsTotal: number;
  /** The read found fewer/more home rows than the manifest's exact count — say so. */
  incomplete?: true;
  availableFrom?: string;
  caveats: BPDashboardCaveat[];
}

// ---------------------------------------------------------------------------
// GET …/dashboard/target-citations — « Cibles citées » card on the Vue d'ensemble
// ---------------------------------------------------------------------------

/** One layer of the stacked citation chart. */
export type BPTargetCitationsSource = BrandQueryAxis | 'conversations';

/**
 * Citations of ONE target category per bucket, one series per source, for a STACKED
 * chart whose top edge is the overall volume.
 *
 * Every value is a citation COUNT (a target cited 10 times counts 10): BP's
 * `BPTOP.byCategory[cat].c` and Conversations' `TDAY#.citByCategory[i]` both count
 * occurrences, which is what makes the layers addable. Raw volume, not a rate: it moves
 * with the number of audits, prompts and launches as well as with visibility.
 *
 * Grain = `selectContentsGrain` (day ≤ 31 d, ISO week ≤ 90 d, month beyond — the monthly
 * tier has no writer, so > 90 d raises `monthly_tier_empty`). `null` = the source was
 * NOT measured in that bucket (no row), never 0; a measured bucket with nothing cited is 0.
 * A source with no row anywhere in the window is OMITTED from `series`.
 */
export interface BPTargetCitationsResponse {
  category: TargetCategory;
  from: string;
  to: string;
  grain: BPContentsGrain;
  clamped?: true;
  /** Bucket keys, ascending: every bucket where at least one source was measured. */
  buckets: string[];
  series: Partial<Record<BPTargetCitationsSource, (number | null)[]>>;
  /** Buckets holding a partial BP audit, or cut by the window edge. */
  partialBuckets?: string[];
  /** Earliest first BP target day across the three axes (`BPTGTFLOOR#`). */
  availableFrom?: string;
  caveats: BPDashboardCaveat[];
}
